import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Router, RouterModule, UrlTree } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Popover } from 'primeng/popover';
import { DateTime } from 'luxon';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { PRIMECONTROL_TOOLTIP } from 'app/shared/directives/primecontrol-tooltip.directive';
import {
  catchError,
  filter,
  finalize,
  fromEvent,
  interval,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { NewNotificationTracker } from 'app/layout/common/notifications/new-notification-tracker';
import { NotificationSoundService } from 'app/shared/services/notification-sound.service';
import { PopUpMenuService } from 'app/shared/services/pop-up-menu.service';
import { NotificationDetail, notificationParts } from 'app/shared/utils/notification-parts';
import { NotificationAction, notificationAction } from 'app/shared/utils/notification-action';
import {
  DayBucket,
  dayBucket,
  relativeTimeLabel,
  RelativeTimeLabel,
} from 'app/shared/utils/relative-time';

/** A notification shaped for display: the row reads these, never the raw record. */
interface NotificationRow {
  source: Notification;
  /** What the notification is about — the document name or folio. */
  subject: string;
  detail: NotificationDetail | null;
  time: RelativeTimeLabel | null;
  icon: string;
  /**
   * An in-app link as a parsed tree, so its query string survives ("?tab=followUp
   * &followUp=…" from "Toca seguimiento"); an array link encoded the "?".
   */
  route: UrlTree | null;
  /** The task the notification asks for, or null when it only informs. */
  action: NotificationAction | null;
}

/** Notifications that happened on the same day, newest first. */
interface NotificationGroup {
  bucket: DayBucket;
  labelKey: string;
  rows: NotificationRow[];
}

const BUCKET_ORDER: DayBucket[] = ['TODAY', 'YESTERDAY', 'EARLIER'];

const BUCKET_LABELS: Record<DayBucket, string> = {
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  EARLIER: 'EARLIER',
};

@Component({
  selector: 'notifications',
  templateUrl: './notifications.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
  imports: [CommonModule, RouterModule, TranslateModule, Popover, SvgIcon, PRIMECONTROL_TOOLTIP],
})
export class NotificationsComponent implements OnInit {
  /**
   * PrimeNG popover — the same mechanism brandbot uses. It auto-positions,
   * appends to <body>, and the responsive width clamp lives in the template
   * styleClass (`w-[min(24rem,calc(100vw-1.5rem))]`), so small screens are
   * handled for free (no CDK overlay + fixed-inset mobile hack).
   */
  @ViewChild('op') popoverRef!: Popover;

  private _notificationsService: NotificationsService = inject(NotificationsService);
  private _popUpMenuService: PopUpMenuService = inject(PopUpMenuService);
  private _router: Router = inject(Router);

  readonly notifications = signal<Notification[]>([]);

  /** The last mark/delete was refused and has been undone. */
  readonly actionFailed = signal(false);

  /**
   * Reference moment for every relative label in the panel, refreshed when it
   * opens. One shared value keeps the list internally consistent (two rows a
   * minute apart never both read "hace 5 min"), and re-reading it per open is
   * enough for a panel that lives for seconds at a time — no interval to leak.
   */
  private readonly _openedAt = signal(DateTime.now());

  /** The unread rows on screen plus the older unread ones the list leaves out. */
  readonly unreadCount = computed(
    () =>
      this.notifications().filter((notification) => !notification.read).length +
      this._notificationsService.unreadBeyondList()
  );

  /**
   * The inbox grouped by calendar day, newest first within each group. Empty
   * groups are dropped so the panel never shows a heading with nothing under it.
   */
  readonly groups = computed<NotificationGroup[]>(() => {
    const now = this._openedAt();
    const rows = this.notifications().map((source) => this._toRow(source, now));
    const byBucket = new Map<DayBucket, NotificationRow[]>();

    for (const row of rows) {
      const bucket = dayBucket(row.source.time, now);
      const existing = byBucket.get(bucket);
      if (existing) {
        existing.push(row);
      } else {
        byBucket.set(bucket, [row]);
      }
    }

    return BUCKET_ORDER.filter((bucket) => byBucket.get(bucket)?.length).map((bucket) => ({
      bucket,
      labelKey: BUCKET_LABELS[bucket],
      rows: this._newestFirst(byBucket.get(bucket)!),
    }));
  });

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
    });
  }

  /** Register with the shared pop-up coordinator so opening any other
   *  header menu (language, user) hides this popover — and vice versa. */
  onPopoverShow(): void {
    // Re-anchor the relative labels: the panel may have been built minutes ago.
    this._openedAt.set(DateTime.now());
    this.actionFailed.set(false);
    this._popUpMenuService.register(this.popoverRef);
    this._refresh();
  }

  onPopoverHide(): void {
    this._popUpMenuService.unregister(this.popoverRef);
  }

  ngOnInit(): void {
    // Load the inbox; the resolver no longer pre-fetches it, so the bell owns
    // its own data. A failure just leaves the bell empty.
    this._refresh();

    this._notificationsService.notifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((notifications: Notification[]) => {
        this.notifications.set(notifications ?? []);
      });

    this._pollWhileVisible();
  }

  /** How often an open tab re-reads the inbox. */
  private static readonly POLL_INTERVAL_MS = 2 * 60 * 1000;

  private _document = inject(DOCUMENT);

  /**
   * Keeps the badge current without a reload: re-reads the inbox every couple of
   * minutes while the tab is in front, and at once when the user comes back to
   * it. A tab in the background asks for nothing — a session left open on a
   * second screen all day would otherwise poll for nobody.
   */
  private _pollWhileVisible(): void {
    const visible = () => this._document.visibilityState === 'visible';

    interval(NotificationsComponent.POLL_INTERVAL_MS)
      .pipe(filter(visible), takeUntil(this._unsubscribeAll))
      .subscribe(() => this._refresh());

    fromEvent(this._document, 'visibilitychange')
      .pipe(filter(visible), takeUntil(this._unsubscribeAll))
      .subscribe(() => this._refresh());
  }

  /** A refresh is in flight. Opening the panel twice in a second would only
   *  race the first request to the same answer. */
  private _refreshing = false;

  /**
   * Re-reads the inbox.
   *
   * The bell used to fetch once, when the component mounted. Notifications are
   * written server-side — by another user acting on a record, or by the
   * scheduled scans that run every few hours — so on a session left open all
   * day the panel kept showing whatever was true at login, and the badge only
   * caught up on a page reload.
   *
   * A failed refresh deliberately leaves the list already on screen alone:
   * emptying it would read as "nothing pending", which is worse than stale.
   */
  private _refresh(): void {
    if (this._refreshing) {
      return;
    }
    this._refreshing = true;

    this._notificationsService
      .getAll()
      .pipe(
        takeUntil(this._unsubscribeAll),
        // A failed load is not an empty inbox: it must not prime the tracker,
        // or the next good load would ring for everything already there.
        catchError(() => of(null as Notification[] | null)),
        finalize(() => (this._refreshing = false))
      )
      .subscribe((loaded) => {
        if (loaded) {
          this._sound.announce(this._arrivals.observe(loaded));
        }
      });
  }

  /** Which loaded notifications are new since the last load, to ring for them. */
  private readonly _arrivals = new NewNotificationTracker();

  private readonly _sound = inject(NotificationSoundService);

  /** The speaker in the panel header shows the saved setting. */
  readonly soundOn = this._sound.savedEnabled;

  /** The speaker: mutes or unmutes at once; turning it on plays the tone as confirmation. */
  toggleSound(): void {
    const on = !this.soundOn();
    this._sound.setEnabled(on);
    if (on) {
      this._sound.preview(this._sound.savedTone());
    }
  }

  /** Close the popover (used by the header close button). */
  close(): void {
    this.popoverRef?.hide();
  }

  markAllAsRead(): void {
    this._run(this._notificationsService.markAllAsRead());
  }

  /**
   * Opening a notification is reading it: it is marked read and the panel
   * closes, so it does not stay floating over the page (or the modal) the link
   * just opened. A row with no link only gets marked.
   */
  open(row: NotificationRow): void {
    this._run(this._notificationsService.markAsRead(row.source));
    if (row.source.link) {
      this.close();
    }
  }

  /**
   * The row's action button ("Programar seguimiento", "Revisar cotización"). It
   * sits inside the row's link, so it stops that click and navigates to its own
   * destination; like opening the row, it reads the notification and closes the panel.
   */
  runAction(row: NotificationRow, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const button = row.action?.button;
    if (!button) {
      return;
    }
    this._run(this._notificationsService.markAsRead(row.source));
    this.close();
    void this._router.navigateByUrl(button.link);
  }

  /**
   * Toggle read state without navigating — the whole row is a link, so the
   * action buttons sitting inside it have to stop the click from reaching it.
   */
  toggleRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this._run(this._notificationsService.toggleRead(notification));
  }

  delete(notification: Notification, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this._run(this._notificationsService.delete(notification));
  }

  /**
   * Runs one of the row/header actions. The service already undid its optimistic
   * change when the request failed; this says so in the panel, since the change
   * the user saw has just been taken back.
   */
  private _run(action: Observable<unknown>): void {
    this.actionFailed.set(false);
    action.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      error: () => this.actionFailed.set(true),
    });
  }

  /**
   * Shapes one record for display: the description split into a subject and a
   * detail, the timestamp as a relative label, and the icon guaranteed to be a
   * name the icon registry knows.
   */
  private _toRow(source: Notification, now: DateTime): NotificationRow {
    const parts = notificationParts(source.description, now);
    return {
      source,
      // Empty when the notification carries no description. The template then
      // promotes the type to the hero line rather than leaving an eyebrow
      // floating over nothing.
      subject: parts?.subject || '',
      detail: parts?.detail ?? null,
      time: relativeTimeLabel(source.time, now),
      icon: source.icon || 'heroicons_outline:bell',
      route: source.useRouter && source.link ? this._router.parseUrl(source.link) : null,
      // Only in-app links can be rewritten into the task's screen.
      action: notificationAction(source.title, source.useRouter ? source.link : undefined),
    };
  }

  /** Newest first, with unparseable timestamps left in their arrival order. */
  private _newestFirst(rows: NotificationRow[]): NotificationRow[] {
    return [...rows].sort((a, b) => {
      const left = DateTime.fromISO(`${a.source.time ?? ''}`, { zone: 'utc' });
      const right = DateTime.fromISO(`${b.source.time ?? ''}`, { zone: 'utc' });
      if (!left.isValid || !right.isValid) {
        return 0;
      }
      return right.toMillis() - left.toMillis();
    });
  }
}
